import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../../../components/MainLayout/MainLayout';
import { Col, Row, Form, Input, Button, Card, notification } from 'antd';
import { colors } from '../../../../utils/colors/colors';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';
import { useTranslation } from 'react-i18next';
import {
    fetchCompanyDetailRequest,
    createUserRequest,
    fetchUserRolesRequest,
    clearCreateUserStatus
} from '../../../../redux/reducers/clients/clientsSlice';
import { UserFormData, UserRole } from '../../../../redux/reducers/clients/clients.types';
import PageHeader from '../../../../components/PageHeader/PageHeader';
import SearchableSelect from '../../../../components/SearchableSelect/SearchableSelect'; // Added import

const AddUser = () => {
    const { t } = useTranslation(['clients', 'common']);
    const [form] = Form.useForm<UserFormData>();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { companyId } = useParams<{ companyId: string }>();
    const breadcrumbCrumbs = [
        { title: 'dashboard', path: '/dashboard' },
        { title: 'clients', path: '/clients' },
        { title: 'add_new_user' }
    ];

    const {
        companyDetail: { data: companyData },
        userRoles: { data: userRolesData },
        createUser: { loading: createLoading, success: createUserSuccess, error: createUserError }
    } = useSelector((state: RootState) => state.clients);

    useEffect(() => {
        if (companyId) {
            dispatch(fetchCompanyDetailRequest(companyId));
        }
        dispatch(fetchUserRolesRequest());
    }, [dispatch, companyId]);

    useEffect(() => {
        if (companyData) {
            form.setFieldsValue({ companyName: companyData.name });
        }
    }, [companyData, form]);

    useEffect(() => {
        if (createUserSuccess) {
            notification.success({
                message: t("user_add_success"),
            });
            form.resetFields();
            dispatch(clearCreateUserStatus());
            setTimeout(() => {
                navigate(`/clients/view-users/${companyId}`);
            }, 1500);
        }
    }, [createUserSuccess, dispatch, navigate, companyId, form, t]);

    useEffect(() => {
        if (createUserError) {
            notification.error({
                message: t(createUserError),
            });
            dispatch(clearCreateUserStatus());
        }
    }, [createUserError, dispatch, t]);

    const onFinish = (values: UserFormData) => {
        if (!companyId) return;
        const { companyName, ...userData } = values;
        dispatch(createUserRequest({ companyId: companyId, data: userData }));
    };

    const genderOptions = [
        { value: 'male', label: t('male') },
        { value: 'female', label: t('female') },
        { value: 'other', label: t('other') }
    ];

    const userRoleOptions = userRolesData?.map((role: UserRole) => ({
        value: role.id,
        label: t(role.name)
    })) || [];


    return (
        <MainLayout>
            <PageHeader crumbs={breadcrumbCrumbs} />
            <Row>
                <Col xs={24}>
                    <Card>
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={onFinish}
                            autoComplete="off"
                        >
                            <Row gutter={24}>
                                <Col xs={12}>
                                    <Form.Item label={t("company_name")} name="companyName">
                                        <Input placeholder={t("company_name")} disabled />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item label={t("name")} name="name" rules={[{ required: true, message: t("name_required") }]}>
                                        <Input placeholder={t("enter_name")} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item label={t("surname")} name="surname" rules={[{ required: true, message: t("surname_required") }]}>
                                        <Input placeholder={t("enter_surname")} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item label={t("email")} name="email" rules={[{ required: true, message: t("email_required") }, { type: 'email', message: t("valid_email_required") }]}>
                                        <Input placeholder={t("enter_mail_address")} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item label={t("gender")} name="gender" rules={[{ required: true, message: t("gender_required") }]}>
                                        <SearchableSelect
                                            placeholder={t("select_gender")}
                                            options={genderOptions}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item label={t("user_role")} name="userRoleId" rules={[{ required: true, message: t("user_role_required") }]}>
                                        <SearchableSelect
                                            placeholder={t("select_user_role")}
                                            options={userRoleOptions}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item
                                        label={t("set_password")}
                                        name="password"
                                        hasFeedback
                                        rules={[
                                            { required: true, message: t("password_required") },
                                            { min: 8, message: t("password_min_length") },
                                            {
                                                validator: (_, value) => {
                                                    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[./@$!%*?&=+\-_#^~|\\<>{}[\]:;"',()])/;
                                                    if (!value || regex.test(value)) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error(t('passwordComplexity')));
                                                },
                                            }
                                        ]}
                                    >
                                        <Input.Password placeholder={t("set_password")} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item
                                        label={t("enter_password_again")}
                                        name="confirmPassword"
                                        dependencies={['password']}
                                        hasFeedback
                                        rules={[
                                            { required: true, message: t("confirm_password_required") },
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    if (!value || getFieldValue('password') === value) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error(t("passwords_do_not_match")));
                                                },
                                            }),
                                        ]}
                                    >
                                        <Input.Password placeholder={t("enter_password_again")} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row justify={"end"}>
                                <Col>
                                    <Button type="primary" style={{ background: colors.blueitLightBlue }} htmlType="submit" size="middle" loading={createLoading}>
                                        {t("save")}
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </Card>
                </Col>
            </Row>
        </MainLayout>
    );
}

export default AddUser;